import {
  GoogleGenerativeAI,
  GenerateContentResult,
  SchemaType,
} from "@google/generative-ai";
import { Menu } from "@/types/menu";
import { consumeCredits } from "@/lib/credits";
import { createClient } from "@/lib/supabase/client";
import { trackEvent, incrementUserProperty, setUserPropertiesOnce } from "@/lib/mixpanel-client";

const apiKey = process.env.NEXT_PUBLIC_GOOGLE_GEMINI_API_KEY!;
const genAI = new GoogleGenerativeAI(apiKey);

const generationConfig = {
  temperature: 0.1,
  topP: 0.8,
  topK: 40,
  maxOutputTokens: 100000,
};

// Define the schema for menu structure
const menuSchema = {
  type: SchemaType.OBJECT,
  properties: {
    originalText: {
      type: SchemaType.STRING,
      description: "Raw text extracted from menu",
    },
    detectedLanguage: {
      type: SchemaType.STRING,
      description: "The dominant language detected in the menu (e.g., 'japanese', 'english', 'korean', etc.)",
    },
    detectedCurrency: {
      type: SchemaType.STRING,
      description: "The currency symbol detected in the menu (e.g., '¥', '$', '€', etc.)",
    },
    menu: {
      type: SchemaType.OBJECT,
      properties: {
        title: {
          type: SchemaType.OBJECT,
          description: "Menu title in detected language",
          properties: {
            text: {
              type: SchemaType.STRING,
              description: "The menu title text in the detected language"
            }
          }
        },
        categories: {
          type: SchemaType.ARRAY,
          items: {
            type: SchemaType.OBJECT,
            properties: {
              id: {
                type: SchemaType.STRING,
                description: "Unique category identifier",
              },
              name: {
                type: SchemaType.OBJECT,
                description: "Category name in detected language",
                properties: {
                  text: {
                    type: SchemaType.STRING,
                    description: "The category name text in the detected language"
                  }
                }
              },
              items: {
                type: SchemaType.ARRAY,
                items: {
                  type: SchemaType.OBJECT,
                  properties: {
                    id: {
                      type: SchemaType.STRING,
                      description: "Unique item identifier",
                    },
                    name: {
                      type: SchemaType.OBJECT,
                      description: "Item name in detected language",
                      properties: {
                        text: {
                          type: SchemaType.STRING,
                          description: "The item name text in the detected language"
                        }
                      }
                    },
                    description: {
                      type: SchemaType.OBJECT,
                      description: "Item description in detected language",
                      properties: {
                        text: {
                          type: SchemaType.STRING,
                          description: "The item description text in the detected language"
                        }
                      }
                    },
                    price: {
                      type: SchemaType.OBJECT,
                      properties: {
                        amount: {
                          type: SchemaType.NUMBER,
                          description: "Regular price amount",
                        },
                        taxIncluded: {
                          type: SchemaType.NUMBER,
                          description: "Tax-included price amount",
                          nullable: true,
                        },
                      },
                      required: ["amount"],
                    },
                  },
                  required: ["id", "name", "price"],
                },
              },
            },
            required: ["id", "name", "items"],
          },
        },
      },
      required: ["title", "categories"],
    },
  },
  required: ["originalText", "detectedLanguage", "detectedCurrency", "menu"],
};

export const geminiModel = genAI.getGenerativeModel({
  model: "gemini-2.5-flash-preview-04-17",
  generationConfig: {
    ...generationConfig,
    responseMimeType: "application/json",
    responseSchema: menuSchema,
  },
});

export type MenuResult = {
  originalText: string;
  detectedLanguage: string;
  detectedCurrency: string;
  menu: {
    title: {
      text: string;
    };
    categories: {
      id: string;
      name: {
        text: string;
      };
      items: {
        id: string;
        name: {
          text: string;
        };
        description?: {
          text: string;
        };
        price: {
          amount: number;
          taxIncluded?: number;
        };
      }[];
    }[];
  };
};

export type ProcessMenuResult = 
  | (MenuResult & { fileName: string })
  | { cancelled: true };

export type UploadProgressCallback = (progress: {
  fileUploaded: boolean;
  analyzing: boolean;
  extracting: boolean;
  creating: boolean;
}) => void;

function sanitizeFileName(fileName: string): string {
  // Get the file extension
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  
  // Create a URL-safe base name by:
  // 1. Encoding the original filename to handle all Unicode characters
  // 2. Removing problematic characters but keeping hyphens and underscores
  const safeName = encodeURIComponent(fileName.slice(0, fileName.lastIndexOf('.')))
    .replace(/%/g, '')
    .replace(/[^a-zA-Z0-9-_]/g, '-')
    .replace(/-+/g, '-') // Replace multiple consecutive hyphens with a single one
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

  // Create a unique filename with timestamp
  const timestamp = Date.now();
  const uniqueName = `${timestamp}-${safeName || 'untitled'}.${extension}`;

  return uniqueName;
}

async function prepareFileData(file: File) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const base64Data = Buffer.from(arrayBuffer).toString("base64");

    // For all files, use inline data
    return {
      inlineData: {
        data: base64Data,
        mimeType: file.type,
      },
    };
  } catch (error) {
    console.error("Error preparing file:", error);
    throw new Error("Failed to prepare file data");
  }
}

type Category = {
  id: string;
  name: {
    japanese: string;
  };
  items: {
    id: string;
    name: {
      japanese: string;
    };
    price: {
      amount: number;
      taxIncluded?: number | null;
    };
  }[];
};

interface TranslatedMenuChunk {
  title?: {
    [key: string]: string;  // Make all language keys optional
  };
  description?: {
    [key: string]: string;  // Make all language keys optional
  };
  categories: {
    id: string;
    name: {
      [key: string]: string;  // Make all language keys optional
    };
    items: {
      id: string;
      name: {
        [key: string]: string;  // Make all language keys optional
      };
      description?: {
        [key: string]: string;  // Make all language keys optional
      };
    }[];
  }[];
}

// Helper function to split menu into smaller chunks
function splitMenuIntoChunks(menu: TranslatedMenuChunk, categoriesPerChunk: number): TranslatedMenuChunk[] {
  const chunks: TranslatedMenuChunk[] = [];
  for (let i = 0; i < menu.categories.length; i += categoriesPerChunk) {
    chunks.push({
      title: i === 0 ? menu.title : undefined, // Only include title in first chunk
      description: i === 0 ? menu.description : undefined, // Only include description in first chunk
      categories: menu.categories.slice(i, i + categoriesPerChunk)
    });
  }
  return chunks;
}

// Helper function to merge translated chunks back together
function mergeTranslatedChunks(chunks: TranslatedMenuChunk[]): TranslatedMenuChunk {
  // Make sure we have at least one chunk
  if (!chunks || chunks.length === 0) {
    return {
      title: { english: "" },
      description: { english: "" },
      categories: []
    };
  }
  
  // Extract title and description from the first chunk (which should contain them)
  const title = chunks[0]?.title || {};
  const description = chunks[0]?.description || {};
  
  // Combine all categories from all chunks
  const categories = chunks.flatMap(chunk => chunk.categories || []);
  
  return {
    title,
    description,
    categories
  };
}

// Helper function to normalize language codes to match our supported languages
function normalizeLanguageCode(languageCode: string): string {
  const normalizedCode = languageCode.toLowerCase();
  
  // Map generic language names to specific supported codes
  if (normalizedCode === 'chinese') {
    return 'chinese-simplified';
  } else if (normalizedCode === 'mandarin') {
    return 'chinese-simplified';
  } else if (normalizedCode === 'cantonese' || 
             normalizedCode === 'japanese' || 
             normalizedCode === 'korean' || 
             normalizedCode === 'thai' ||  // Thai language is already supported
             normalizedCode === 'vietnamese' || 
             normalizedCode === 'indonesian' || 
             normalizedCode === 'malay' || 
             normalizedCode === 'filipino' || 
             normalizedCode === 'hindi' ||
             normalizedCode === 'bhojpuri' ||
             normalizedCode === 'arabic' ||
             normalizedCode === 'persian' ||
             normalizedCode === 'hebrew' ||
             normalizedCode === 'turkish' ||
             normalizedCode === 'czech' ||
             normalizedCode === 'dutch' || 
             normalizedCode === 'french' || 
             normalizedCode === 'german' || 
             normalizedCode === 'italian' ||
             normalizedCode === 'polish' ||
             normalizedCode === 'portuguese' ||
             normalizedCode === 'portuguese-brazilian' ||
             normalizedCode === 'romanian' || 
             normalizedCode === 'russian' ||
             normalizedCode === 'slovenian' ||
             normalizedCode === 'spanish' ||
             normalizedCode === 'swedish') {
    return normalizedCode;
  } else if (normalizedCode.startsWith('chinese-')) {
    // Handle chinese-simplified and chinese-traditional
    return normalizedCode;
  } else if (normalizedCode === 'brazilian portuguese' || normalizedCode === 'pt-br' || normalizedCode === 'portuguese (brazilian)') {
    return 'portuguese-brazilian';
  }
  
  // Default to English for all other languages or if language detection fails
  return 'english';
}

export async function processMenuImage(
  file: File, 
  signal?: AbortSignal,
  onProgress?: UploadProgressCallback
): Promise<ProcessMenuResult> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error("User not authenticated");
  }

  // Fetch user profile to determine if this is the first menu
  let isFirstMenuCreated = false; // Default to false
  const { data: profileData, error: profileError } = await supabase
    .from('profiles')
    .select('menus_created_count') 
    .eq('id', user.id)
    .single();

  if (profileError && profileError.code !== 'PGRST116') { // PGRST116: row not found
    console.error('Error fetching profile for menus_created_count:', profileError);
    // Not throwing, will proceed with isFirstMenuCreated = false or rely on Mixpanel's increment nature
  } else {
    const menusCreatedCount = profileData?.menus_created_count || 0;
    isFirstMenuCreated = menusCreatedCount === 0;
  }

  // Track upload progress, filename, and processing time
  let fileUploaded = false;
  const fileName = sanitizeFileName(file.name);  // Use the sanitized filename
  const startTime = Date.now(); // Track start time for duration calculation
  
  // Check if user has enough credits (1 credit for menu processing)
  const hasCredits = await consumeCredits(user.id, 1);
  if (!hasCredits) {
    throw new Error("Insufficient credits. Please upgrade your plan or purchase additional credits.");
  }

  // Check if already aborted
  if (signal?.aborted) {
    return { cancelled: true };
  }

  try {
    const fileData = await prepareFileData(file);

    // Add PDF size validation
    if (file.type === "application/pdf" && file.size > 20 * 1024 * 1024) {
      throw new Error("PDF files must be under 20MB");
    }

    // Upload the original image to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from("menus")
      .upload(fileName, file);

    if (uploadError) throw uploadError;
    fileUploaded = true;

    // Notify that file is uploaded and processing is starting
    onProgress?.({
      fileUploaded: true,
      analyzing: true,
      extracting: false,
      creating: false,
    });

    // Check if aborted after file upload
    if (signal?.aborted) {
      // Clean up the uploaded file
      await supabase.storage.from("menus").remove([fileName]);
      return { cancelled: true };
    }

    const prompt = `Extract ALL menu items from this ${
      file.type === "application/pdf" ? "PDF" : "image"
    } and return them in a structured format. Look at EVERY section of the image, including edges, sides, and all visible areas.

Important notes:
1. VERY IMPORTANT: Process the ENTIRE menu image, including ALL visible sections, columns, boxes, and formatted areas. Don't focus only on the center or most prominent section.
2. Thoroughly scan and process ALL menu items across the ENTIRE image, including items in different colors, backgrounds, or formatting.
3. VERY IMPORTANT: Look for and extract the restaurant name if it appears anywhere in the menu (header, footer, watermark, logo, etc.) and use it as the menu title. If the restaurant name is "Golden Dragon" or similar, make sure to set that as the menu title.
4. If no explicit restaurant name is found, use the most logical title based on menu content (e.g., "Dim Sum Menu", "Lunch Specials", etc.).
5. VERY IMPORTANT: Determine the dominant language of the actual TEXT in the menu (not the type of cuisine). For example, if a Chinese restaurant menu is written in English, the detectedLanguage should be "english" NOT "chinese".
6. When determining the language, ignore foreign food names, restaurant names, or cuisine-specific terms - focus only on the primary language used for descriptions and headers.
7. Detect and identify the currency symbol used in the menu prices (e.g., '¥', '$', '€', etc.) and include it in the detectedCurrency field.
8. For each text field (title, category names, item names, descriptions), provide the text in the detected language in the 'text' property.
9. Extract both regular price (amount) and tax-included price (taxIncluded) when available.
10. For prices without tax shown, only include the amount field.
11. Generate unique IDs for categories and items.
12. Keep responses concise and focused on essential menu information.
13. Include item descriptions when available in the menu.
14. Process ALL items across the ENTIRE menu - do not limit extraction to a subset of items.
15. NEVER leave the menu title empty or null. If no title is found, use a sensible default like "Restaurant Menu" or a title based on the dominant food category.

Example response structure:
{
  "originalText": "Raw extracted text...",
  "detectedLanguage": "english",
  "detectedCurrency": "£",
  "menu": {
    "title": {
      "text": "Golden Dragon Restaurant"
    },
    "categories": [
      {
        "id": "cat-1",
        "name": {
          "text": "Steamed Dim Sum"
        },
        "items": [
          {
            "id": "item-1",
            "name": {
              "text": "Crab Meat & Prawn Dumpling in Soup"
            },
            "price": {
              "amount": 4.30
            }
          },
          {
            "id": "item-2",
            "name": {
              "text": "Har Kau King Prawn Dumplings"
            },
            "price": {
              "amount": 3.90
            }
          }
        ]
      },
      {
        "id": "cat-2",
        "name": {
          "text": "Fried Dim Sum"
        },
        "items": [
          {
            "id": "item-3",
            "name": {
              "text": "Paper Wrapped Prawns"
            },
            "price": {
              "amount": 4.10
            }
          }
        ]
      }
    ]
  }
}`;

    // Create a controller for the Gemini request
    const controller = new AbortController();

    // If parent signal is aborted, abort our controller too
    if (signal) {
      signal.addEventListener('abort', () => {
        controller.abort();
      }, { once: true });
    }

    const contentPromise = geminiModel.generateContent([
      fileData,
      { text: prompt },
    ], { signal: controller.signal });

    const timeoutPromise = new Promise((_, reject) => {
      const timeoutId = setTimeout(() => {
        controller.abort();
        reject(new Error("Menu processing timed out"));
      }, 600000);

      // Clean up timeout if aborted
      if (signal) {
        signal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
        }, { once: true });
      }
    });

    // Race between content generation and timeout
    const result = await Promise.race([
      contentPromise,
      timeoutPromise,
    ]) as GenerateContentResult;

    // Check if aborted after Gemini request
    if (signal?.aborted) {
      // Clean up the uploaded file
      await supabase.storage.from("menus").remove([fileName]);
      return { cancelled: true };
    }

    // Get the raw response text
    const rawText = await result.response.text();

    // Extract the JSON part from the response
    const jsonMatch = rawText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error("No valid JSON found in response");
    }

    const text = jsonMatch[0]
      .replace(/^```json\s*|\s*```$/g, "") // Remove any code block markers
      .replace(/[\u200B-\u200D\uFEFF]/g, "") // Remove zero-width spaces
      .replace(/\n\s*\n/g, "\n") // Remove empty lines
      .replace(/,(\s*[}\]])/g, "$1") // Remove trailing commas
      .trim();

    try {
      const parsedResponse = JSON.parse(text);

      // Check if the response has the expected structure
      if (!parsedResponse.menu || !parsedResponse.originalText) {
        console.error("Invalid response structure:", parsedResponse);
        throw new Error("Invalid menu structure");
      }

      // Ensure menu has required fields
      if (!parsedResponse.menu.title || !parsedResponse.menu.categories) {
        console.error("Missing required menu fields:", parsedResponse.menu);
        throw new Error("Invalid menu structure");
      }

      // Convert null taxIncluded values to undefined
      parsedResponse.menu.categories = parsedResponse.menu.categories.map(
        (category: Category) => ({
          ...category,
          items: category.items.map((item) => ({
            ...item,
            price: {
              ...item.price,
              taxIncluded: item.price.taxIncluded || undefined,
            },
          })),
        })
      );

      // Analyze the menu content to verify language detection
      const detectedLanguage = parsedResponse.detectedLanguage?.toLowerCase() || "english";
      
      // Extract sample text from the menu for language validation
      const menuTexts: string[] = [];
      
      // Add title text
      if (parsedResponse.menu.title?.text) {
        menuTexts.push(parsedResponse.menu.title.text);
      }
      
      // Add category names and item descriptions 
      for (const category of parsedResponse.menu.categories) {
        if (category.name?.text) {
          menuTexts.push(category.name.text);
        }
        
        for (const item of category.items) {
          if (item.name?.text) {
            menuTexts.push(item.name.text);
          }
          if (item.description?.text) {
            menuTexts.push(item.description.text);
          }
        }
      }
      
      // Join sample texts and perform validation
      const sampleText = menuTexts.slice(0, 10).join(" ");
      
      // Force English detection if sample text mostly uses Latin alphabet
      const englishCharsRegex = /[a-zA-Z\s.,!?&()$£€¥]/g;
      const totalLength = sampleText.length;
      const englishCharsLength = (sampleText.match(englishCharsRegex) || []).length;
      const englishRatio = totalLength > 0 ? englishCharsLength / totalLength : 0;
      
      // If 70% or more characters are English and model returned "chinese", correct to "english"
      let correctedLanguage = detectedLanguage;
      if (englishRatio > 0.7 && detectedLanguage === "chinese") {
        console.log(`Language detection corrected: "${detectedLanguage}" → "english" (${Math.round(englishRatio * 100)}% English characters)`);
        correctedLanguage = "english";
        // Update the detected language in the response
        parsedResponse.detectedLanguage = correctedLanguage;
      }

      // Calculate the total number of items
      let totalItems = 0;
      for (const category of parsedResponse.menu.categories) {
        totalItems += category.items.length;
      }

      // Success case - increment the credit usage in the database
      Object.assign(parsedResponse, { fileName });

      // Track Menu Process Succeeded event
      trackEvent('Menu Process Succeeded', {
        'User ID': user.id,
        'Detected Language': parsedResponse.detectedLanguage,
        'Number of Categories': parsedResponse.menu.categories.length,
        'Number of Items': totalItems,
        'Processing Duration (s)': Math.floor((Date.now() - startTime) / 1000),
        'Is First Menu Created': isFirstMenuCreated,
        'Menu ID': fileName,
      });

      // Track menu creation by incrementing Menus Created Count in Mixpanel
      incrementUserProperty('Menus Created Count', 1);

      // Set 'Time to First Menu (seconds)' if it's the first menu
      if (isFirstMenuCreated && user.created_at) {
        const userCreatedAt = new Date(user.created_at).getTime();
        const timeNow = Date.now();
        const timeToFirstMenuSeconds = Math.floor((timeNow - userCreatedAt) / 1000);
        
        setUserPropertiesOnce({ 
          'Time to First Menu (seconds)': timeToFirstMenuSeconds 
        });
      }

      // Also update Supabase profiles table for menus_created_count
      // This makes the isFirstMenuCreated check reliable for subsequent calls.
      const currentMenusCountForDB = profileData?.menus_created_count || 0;
      const { error: updateProfileError } = await supabase
        .from('profiles')
        .update({ menus_created_count: currentMenusCountForDB + 1 })
        .eq('id', user.id);

      if (updateProfileError) {
        console.error('Error updating menus_created_count in profile:', updateProfileError);
      }

      return parsedResponse;
    } catch (parseError) {
      console.error("JSON Parse Error:", parseError);
      console.error("Raw text causing error:", text);
      throw new Error(
        "Failed to parse menu structure. Please try again with a clearer image or fewer items."
      );
    }
  } catch (error) {
    // If the error is due to abortion or cancellation, handle it gracefully
    if (
      error instanceof Error && (
        error.name === "AbortError" ||
        error.message === "Upload cancelled" ||
        error.message.includes("signal is aborted") ||
        (error.message.includes("Error fetching") && error.message.includes("signal is aborted"))
      )
    ) {
      // Clean up any partial uploads if needed
      if (fileUploaded) {
        try {
          await supabase.storage.from("menus").remove([fileName]);
        } catch (cleanupError) {
          console.error("Error cleaning up uploaded file:", cleanupError);
        }
      }
      return { cancelled: true };
    }

    // For other errors, log and rethrow
    console.error("Error processing menu:", error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Failed to process menu. Please try again.");
  }
}

export async function translateMenu(
  menu: Menu,
  targetLanguage: string,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal
): Promise<Menu> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error("User not authenticated");
  }

  try {
    // Check if the operation was already aborted
    if (signal?.aborted) {
      // Track translation cancelled
      trackEvent('Bulk Translation Cancelled', {
        'Menu ID': menu.id,
        'Source Language': menu.supportedLanguages[0],
        'Stage': 'API Call',
        'Languages In Progress': targetLanguage
      });
      
      throw new Error("Translation cancelled");
    }

    // Check if user has enough credits (1 credit per API request)
    const hasCredits = await consumeCredits(user.id, 1);
    if (!hasCredits) {
      throw new Error("Insufficient credits. Please upgrade your plan or purchase additional credits.");
    }

    const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash-preview-04-17" });
    
    // Make sure source language is properly normalized
    const sourceLanguage = normalizeLanguageCode(menu.supportedLanguages[0]);
    
    // Make sure target language is properly normalized
    const normalizedTargetLanguage = normalizeLanguageCode(targetLanguage);

    // Log top-level menu description for debugging
    console.log("Original menu description:", menu.description);

    // Create a simplified menu with ONLY source language text to translate
    const menuToTranslate: TranslatedMenuChunk = {
      title: menu.title ? {
        [sourceLanguage]: menu.title[sourceLanguage]
      } : undefined,
      // Include menu description if it exists 
      description: menu.description ? {
        [sourceLanguage]: menu.description[sourceLanguage]
      } : undefined,
      categories: menu.categories.map(category => ({
        id: category.id,
        name: {
          [sourceLanguage]: category.name[sourceLanguage]
        },
        items: category.items.map(item => ({
          id: item.id,
          name: {
            [sourceLanguage]: item.name[sourceLanguage]
          },
          description: item.description ? {
            [sourceLanguage]: item.description[sourceLanguage]
          } : undefined
        }))
      }))
    };

    // Log the menuToTranslate to verify description is included
    console.log("Menu to translate structure:", 
                JSON.stringify({
                  hasTitle: !!menuToTranslate.title,
                  hasDescription: !!menuToTranslate.description,
                  categoryCount: menuToTranslate.categories.length
                }));

    const prompt = `Translate this menu from ${sourceLanguage} to ${normalizedTargetLanguage}. Keep the original structure and only translate text fields.
    
Menu to translate:
${JSON.stringify(menuToTranslate, null, 2)}

Important:
1. Preserve all IDs exactly as they are
2. Only translate ${sourceLanguage} text to ${normalizedTargetLanguage}
3. Return valid JSON with the same structure - keep the exact same fields
4. Ensure translations are natural and contextually appropriate for a menu
5. If a description exists (either for menu or items), translate it maintaining the same tone and style
6. Include ALL menu items and categories in your response - do not skip any items
7. Make sure the language key in output is "${normalizedTargetLanguage}" not "${sourceLanguage}"
8. Ensure proper JSON format with no syntax errors
9. Preserve any description fields at all levels - they are important`;

    // For very large menus, we should split into chunks
    // Use smaller chunks to prevent missing translations
    const categoriesPerChunk = 3; // Reduced from 5 to 3 for more reliable translation
    const chunks = splitMenuIntoChunks(menuToTranslate, categoriesPerChunk);
    
    console.log(`Split menu into ${chunks.length} chunks`);
    
    // Check if first chunk has description
    if (chunks.length > 0) {
      console.log("First chunk has description:", !!chunks[0].description);
    }
    
    const translatedChunks = [];

    // Special handling for right-to-left languages like Arabic and Persian
    const isRTLLanguage = ['arabic', 'persian', 'hebrew', 'urdu'].includes(normalizedTargetLanguage);
    
    // Adjust prompt for RTL languages
    let translationPrompt = prompt;
    if (isRTLLanguage) {
      translationPrompt = `Translate this menu from ${sourceLanguage} to ${normalizedTargetLanguage}. Keep the original structure and only translate text fields.
    
Menu to translate:
${JSON.stringify(menuToTranslate, null, 2)}

Important:
1. Preserve all IDs exactly as they are
2. Only translate ${sourceLanguage} text to ${normalizedTargetLanguage}
3. Return valid JSON with the same structure - keep the exact same fields
4. For RIGHT-TO-LEFT languages like ${normalizedTargetLanguage}, ensure proper text direction and character handling
5. Ensure translations are natural and contextually appropriate for a menu
6. If a description exists (either for menu or items), translate it maintaining the same tone and style
7. Include ALL menu items and categories in your response - do not skip any items
8. Make sure the language key in output is "${normalizedTargetLanguage}" not "${sourceLanguage}"
9. Ensure proper JSON format with no syntax errors
10. Preserve any description fields at all levels - they are important
11. CRITICALLY IMPORTANT: Maintain the same number of items in each category`;
    }

    for (let i = 0; i < chunks.length; i++) {
      // Check if the operation was aborted before processing this chunk
      if (signal?.aborted) {
        console.log("Translation cancelled - stopping chunk processing");
        throw new Error("Translation cancelled");
      }

      const chunk = chunks[i];
      // Add retry logic for chunk translation
      let retries = 0;
      const maxRetries = 3; // Increased from 2 to 3 for RTL languages
      let success = false;
      let translatedChunk;
      
      // Log if this chunk contains a description (should be true for first chunk)
      console.log(`Chunk ${i} has description:`, !!chunk.description);
      
      while (!success && retries <= maxRetries) {
        try {
          // Check if the operation was aborted before each retry
          if (signal?.aborted) {
            console.log("Translation cancelled - stopping chunk processing");
            throw new Error("Translation cancelled");
          }

          // Use the appropriate prompt based on language
          const chunkPrompt = translationPrompt.replace(
            JSON.stringify(menuToTranslate, null, 2), 
            JSON.stringify(chunk, null, 2)
          );
          
          // For RTL languages, adjust the model to use a more capable version
          const modelToUse = isRTLLanguage 
            ? genAI.getGenerativeModel({ model: "gemini-2.5-flash-preview-04-17" }) // Use more powerful model for complex scripts
            : model;
            
          // Create a controller for this specific API call
          const controller = new AbortController();
          
          // Link it to the parent signal if provided
          if (signal) {
            // Use a proper abort handling method
            const abortHandler = () => {
              console.log("Parent signal aborted, aborting child controller");
              controller.abort();
            };
            
            // Register the handler
            signal.addEventListener('abort', abortHandler, { once: true });
            
            // Check if already aborted
            if (signal.aborted) {
              controller.abort();
              throw new Error("Translation cancelled");
            }
          }
            
          const result = await modelToUse.generateContent(chunkPrompt, { signal: controller.signal });
          const response = await result.response.text();

          // Extract JSON from response
          const jsonMatch = response.match(/\{[\s\S]*\}/);
          if (!jsonMatch) {
            throw new Error("No valid JSON found in response");
          }

          // Clean and parse the JSON
          let jsonText = jsonMatch[0]
            .replace(/^```json\s*|\s*```$/g, "") // Remove code block markers
            .replace(/[\u200B-\u200D\uFEFF]/g, "") // Remove zero-width spaces
            .replace(/\n\s*\n/g, "\n") // Remove empty lines
            .replace(/,(\s*[}\]])/g, "$1"); // Remove trailing commas
          
          // Fix any unescaped quotes within strings that might break JSON
          // This regex attempts to find straight quotes inside JSON string values
          jsonText = jsonText.replace(/:\s*"([^"]*)"/g, (match, p1) => {
            // Replace any unescaped quotes in the string with escaped quotes
            const fixedContent = p1.replace(/(?<!\\)"/g, '\\"');
            return `: "${fixedContent}"`;
          });
          
          // Additional fixes for common JSON errors
          jsonText = jsonText
            // Fix missing quotes around property names
            .replace(/([{,]\s*)([a-zA-Z0-9_]+)(\s*:)/g, '$1"$2"$3')
            // Fix trailing commas in arrays or objects
            .replace(/,(\s*[\]}])/g, '$1')
            // Remove any non-breaking spaces
            .replace(/\u00A0/g, ' ')
            .trim();
          
          try {
            translatedChunk = JSON.parse(jsonText);
          } catch (parseError) {
            console.error(`JSON Parse Error in chunk ${i}:`, parseError);
            console.error("Problematic JSON:", jsonText);
            
            // Try a more aggressive approach to fix JSON
            try {
              // Use a library like JSON5 if available, or try this simpler approach
              // that attempts to fix common syntax errors
              const sanitizedJson = jsonText
                // Replace single quotes with double quotes
                .replace(/'/g, '"')
                // Remove any invalid control characters
                .replace(/[\x00-\x1F\x7F-\x9F]/g, '');
              
              translatedChunk = JSON.parse(sanitizedJson);
              console.log("Recovered using sanitized JSON");
            } catch {
              // If all attempts fail, rethrow the original error
              throw parseError;
            }
          }
          
          // Log if translated chunk has description
          console.log(`Translated chunk ${i} has description:`, !!translatedChunk.description);
          
          // Verify the chunk has the expected structure
          if (!translatedChunk.categories || 
              !Array.isArray(translatedChunk.categories) || 
              translatedChunk.categories.length === 0) {
            throw new Error("Invalid translation response: missing or empty categories");
          }
          
          // Special handling for RTL languages item count verification
          if (isRTLLanguage) {
            let totalItemsOriginal = 0;
            let totalItemsTranslated = 0;
            
            // Check item counts at category level while allowing for category structure differences
            for (const category of chunk.categories) {
              totalItemsOriginal += category.items.length;
            }
            
            for (const category of translatedChunk.categories) {
              totalItemsTranslated += category.items.length;
            }
            
            // Log the item counts for debugging
            console.log(`Chunk ${i}: Original items: ${totalItemsOriginal}, Translated items: ${totalItemsTranslated}`);
            
            // Allow the translation if the total item count matches, even if categories differ slightly
            if (totalItemsOriginal !== totalItemsTranslated) {
              throw new Error(`Item count mismatch: expected ${totalItemsOriginal}, got ${totalItemsTranslated}`);
            }
          } else {
            // For non-RTL languages, use the original strict category-by-category checking
            for (const category of translatedChunk.categories) {
              // Make sure we have the target language in the translation
              if (!Object.keys(category.name).includes(normalizedTargetLanguage)) {
                throw new Error("Incomplete translation: missing target language");
              }
              
              // Verify items match the original chunk
              const originalCategory = chunk.categories.find(c => c.id === category.id);
              if (!originalCategory || category.items.length !== originalCategory.items.length) {
                throw new Error("Incomplete translation: item count mismatch");
              }
            }
          }
          
          // Check for description field preservation in first chunk
          if (i === 0 && chunk.description && !translatedChunk.description) {
            console.warn("Warning: Description field was lost in translation for first chunk");
            
            // Try to recover the description
            if (chunk.description[sourceLanguage]) {
              // Create a separate translation just for the description
              const descriptionPrompt = `Translate this menu description from ${sourceLanguage} to ${normalizedTargetLanguage}.
                
              Text to translate: "${chunk.description[sourceLanguage]}"
                
              Return only the translated text, no formatting or explanation.`;
              
              try {
                const descResult = await model.generateContent(descriptionPrompt);
                const translatedDesc = await descResult.response.text();
                
                // Add the translated description to the chunk
                translatedChunk.description = {
                  [normalizedTargetLanguage]: translatedDesc.trim()
                };
                
                console.log("Recovered description through separate translation");
              } catch (descError) {
                console.error("Failed to recover description:", descError);
              }
            }
          }
          
          success = true;
        } catch (error) {
          // First, check if this is an abort-related error
          if (
            error instanceof Error && (
              error.name === "AbortError" ||
              error.message === "Translation cancelled" ||
              error.message.includes("cancelled") ||
              error.message.includes("abort") ||
              error.message.includes("signal is aborted") ||
              (error.message.includes("Error fetching") && error.message.includes("signal is aborted"))
            )
          ) {
            // Don't increment retries and don't log error for aborted requests
            console.log(`Translation chunk ${i} cancelled by user.`);
            // Re-throw with standardized message to prevent further retries
            throw new Error("Translation cancelled");
          }
          
          // For non-abort errors, continue with normal retry handling
          retries++;
          console.error(`Translation chunk ${i} failed (attempt ${retries}):`, error);
          
          if (retries > maxRetries) {
            throw new Error(`Failed to translate menu chunk after ${maxRetries} attempts`);
          }
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      translatedChunks.push(translatedChunk);

      // Update progress
      onProgress?.(Math.round(((i + 1) / chunks.length) * 100));
    }

    // Merge chunks back together
    const translatedMenu = mergeTranslatedChunks(translatedChunks);
    
    // Log merged translated menu structure
    console.log("Merged translated menu:", 
                JSON.stringify({
                  hasTitle: !!translatedMenu.title,
                  hasDescription: !!translatedMenu.description,
                  descriptionContent: translatedMenu.description, // Log actual content
                  categoryCount: translatedMenu.categories.length
                }));

    // Merge the translations back into the original menu structure
    const updatedMenu: Menu = { ...menu }; // Start with a clone

    // Merge title
    if (menu.title) {
      updatedMenu.title = { ...menu.title }; // Keep existing
      const translatedTitleText = translatedMenu.title?.[normalizedTargetLanguage];
      if (translatedTitleText?.trim()) {
        updatedMenu.title[normalizedTargetLanguage] = translatedTitleText;
      } else {
        console.warn(`Title translation missing or empty for ${normalizedTargetLanguage}.`);
      }
    }

    // Merge description
    if (menu.description) { // Only proceed if original menu had description
      updatedMenu.description = { ...menu.description }; // Keep existing
      const translatedDescText = translatedMenu.description?.[normalizedTargetLanguage];

      // Log the translated description text found during merge
      console.log(`[Merge] Translated description text found for ${normalizedTargetLanguage}:`, translatedDescText);

      if (translatedDescText?.trim()) {
        updatedMenu.description[normalizedTargetLanguage] = translatedDescText;
      } else {
        console.warn(`[Merge] Menu description translation missing or empty for ${normalizedTargetLanguage}. Will attempt fallback later.`);
        // Ensure the target language key is removed if translation failed/was empty
        if (updatedMenu.description) {
          delete updatedMenu.description[normalizedTargetLanguage];
        }
      }
    } else {
      // Ensure description remains undefined if original menu didn't have one
      updatedMenu.description = undefined;
    }

    // Merge categories
    updatedMenu.categories = menu.categories.map((category) => {
      // Find the corresponding translated category by ID
      const translatedCategory = translatedMenu.categories.find(tc => tc.id === category.id);
      
      if (!translatedCategory) {
        console.error(`[Merge] Missing translation for category: ${category.id}`);
        return category; // Return original if no translation found
      }
      
      const updatedCategory = { ...category };
      
      // Merge category name
      const translatedCatName = translatedCategory.name?.[normalizedTargetLanguage];
      if (translatedCatName?.trim()) {
        updatedCategory.name = { ...category.name, [normalizedTargetLanguage]: translatedCatName };
      } else {
         console.warn(`[Merge] Category name translation missing for ${category.id} in ${normalizedTargetLanguage}.`);
         updatedCategory.name = { ...category.name }; // Keep original
      }
      
      // Merge items
      updatedCategory.items = category.items.map((item) => {
        // Find the corresponding translated item by ID
        const translatedItem = translatedCategory.items.find(ti => ti.id === item.id);
        
        if (!translatedItem) {
          console.error(`[Merge] Missing translation for item: ${item.id}`);
          return item; // Return original if no translation found
        }
        
        const updatedItem = { ...item };
        
        // Merge item name
        const translatedItemName = translatedItem.name?.[normalizedTargetLanguage];
        if (translatedItemName?.trim()) {
          updatedItem.name = { ...item.name, [normalizedTargetLanguage]: translatedItemName };
        } else {
          console.warn(`[Merge] Item name translation missing for ${item.id} in ${normalizedTargetLanguage}.`);
           updatedItem.name = { ...item.name }; // Keep original
        }
        
        // Merge item description
        if (item.description) { // If original item had description
           const translatedItemDesc = translatedItem.description?.[normalizedTargetLanguage];
           if (translatedItemDesc?.trim()) {
              updatedItem.description = { ...item.description, [normalizedTargetLanguage]: translatedItemDesc };
           } else {
              console.warn(`[Merge] Item description translation missing for ${item.id} in ${normalizedTargetLanguage}.`);
              updatedItem.description = { ...item.description }; // Keep original
              // Ensure target language key is removed if translation failed
              delete updatedItem.description[normalizedTargetLanguage];
           }
        } else {
           updatedItem.description = undefined; // Ensure remains undefined if original had none
        }
            
        return updatedItem;
      });
      
      return updatedCategory;
    });

    // Update supported languages
    updatedMenu.supportedLanguages = [
      ...new Set([...menu.supportedLanguages, normalizedTargetLanguage])
    ];
    
    // Final check and fallback for menu description
    // Check if:
    // 1. Original menu had a description
    // 2. Original description had text in the source language
    // 3. The target language translation is missing or empty in the updated menu description
    if (menu.description &&
        menu.description[sourceLanguage] &&
        (!updatedMenu.description || !updatedMenu.description[normalizedTargetLanguage]?.trim()))
    {
      console.warn("[Fallback] Menu description translation is missing or empty - trying fallback direct translation");
      
      // Fallback: Do a separate direct translation for the menu description
      try {
        const descriptionPrompt = `Translate this menu description from ${sourceLanguage} to ${normalizedTargetLanguage}.
          
        Text to translate: "${menu.description[sourceLanguage]}"
          
        Return only the translated text, no formatting or explanation.`;
        
        const descResult = await model.generateContent(descriptionPrompt);
        const translatedDesc = await descResult.response.text();
        const trimmedDesc = translatedDesc.trim();
        
        // Add the translated description to the final menu only if it's not empty
        if (trimmedDesc) {
          updatedMenu.description = {
            ...(updatedMenu.description || {}), // Ensure description object exists
            [normalizedTargetLanguage]: trimmedDesc
          };
          console.log("[Fallback] Successfully recovered menu description through fallback translation");
        } else {
           console.warn("[Fallback] Fallback translation resulted in empty description.");
        }
        
      } catch (descError) {
        console.error("[Fallback] Failed to recover menu description with fallback:", descError);
      }
    }

    // Track successful bulk translation
    trackEvent('Bulk Translation Completed', {
      'Menu ID': menu.id, // From input menu
      'Source Language': sourceLanguage, // Calculated in function
      'Target Languages Succeeded': [normalizedTargetLanguage], // Current function translates one target lang at a time
      'Number of Languages Succeeded': 1,
      // User ID is implicitly added by the client-side SDK if user is identified
    });

    return updatedMenu;
  } catch (error) {
    // Check if this is an abort-related error, and if so, throw a standardized error
    if (
      error instanceof Error && (
        error.name === "AbortError" ||
        error.message === "Translation cancelled" ||
        error.message === "Upload cancelled" ||
        error.message.includes("signal is aborted") ||
        (error.message.includes("Error fetching") && error.message.includes("signal is aborted"))
      )
    ) {
      // For abort errors, don't log the error to console
      console.log(`Translation cancelled by user.`);
      // Throw a standardized "Translation cancelled" error for all abort-related errors
      throw new Error("Translation cancelled");
    }
    
    // Only log non-abort errors
    console.error("Translation error:", error);
    // For other errors, throw a generic message
    throw new Error("Failed to translate menu. Please try again.");
  }
}

export async function translateText(
  text: string,
  sourceLanguage: string,
  targetLanguage: string,
  translationType: 'title' | 'category' | 'item_name' | 'item_description' | 'menu_description'
): Promise<string> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error("User not authenticated");
  }

  // One credit per API request
  const hasCredits = await consumeCredits(user.id, 1);
  if (!hasCredits) {
    throw new Error("Insufficient credits. Please upgrade your plan or purchase additional credits.");
  }

  const startTime = Date.now();
  
  try {
    const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash-preview-04-17" });

    const prompt = `Translate this ${translationType.replace('_', ' ')} from ${sourceLanguage} to ${targetLanguage}. 
Keep the translation natural and contextually appropriate for a menu.

Text to translate: "${text}"

Important:
1. Only return the translated text, no additional formatting or explanation
2. Maintain the same tone and style
3. Ensure the translation is appropriate for a restaurant menu context`;

    const result = await model.generateContent(prompt);
    const translation = await result.response.text();

    // Clean up the response (remove quotes if present)
    const cleanedTranslation = translation.replace(/^["']|["']$/g, '').trim();
    
    // Track successful translation
    trackEvent('Individual Text Translated', {
      'Source Language': sourceLanguage,
      'Target Language(s)': targetLanguage,
      'Translation Type': translationType,
      'Text Length (Source)': text.length,
      'Text Length (Translated)': cleanedTranslation.length,
      'Duration (s)': Math.round((Date.now() - startTime) / 1000),
      'Batch Request?': false
    });
    
    // Track credit usage for individual translation
    trackEvent('Credits Consumed', {
      'Amount': 1,
      'Reason': 'Individual Text Translation',
      'User ID': user.id
    });
    
    // Increment total translations performed
    trackEvent('User Property Updated', {
      'Property': 'Total Translations Performed',
      'Increment By': 1
    });
    
    return cleanedTranslation;
  } catch (error) {
    console.error("Translation error:", error);
    
    // Track failed translation
    trackEvent('Individual Text Translation Failed', {
      'Source Language': sourceLanguage,
      'Target Language(s)': targetLanguage,
      'Translation Type': translationType,
      'Text Length (Source)': text.length,
      'Error Message': error instanceof Error ? error.message : "Unknown error",
      'Duration (s)': Math.round((Date.now() - startTime) / 1000),
      'Batch Request?': false
    });
    
    throw new Error("Failed to translate text. Please try again.");
  }
}

/**
 * Batch translates multiple texts to multiple languages in a single API call.
 * 
 * @param textsToTranslate Array of {text, type} objects to translate
 * @param sourceLanguage Source language code
 * @param targetLanguages Array of target language codes
 * @returns Object with keys for each target language, containing objects with keys for each text type
 */
export async function batchTranslateText(
  textsToTranslate: Array<{text: string, type: 'title' | 'category' | 'item_name' | 'item_description' | 'menu_description'}>,
  sourceLanguage: string,
  targetLanguages: string[]
): Promise<Record<string, Record<string, string>>> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error("User not authenticated");
  }

  // Only consume one credit regardless of how many texts or languages
  const hasCredits = await consumeCredits(user.id, 1);
  if (!hasCredits) {
    throw new Error("Insufficient credits. Please upgrade your plan or purchase additional credits.");
  }
  
  const startTime = Date.now();

  try {
    const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash-preview-04-17" });

    // Create a detailed structured prompt for batch translation
    const textsForPrompt = textsToTranslate.map((item, index) => 
      `Text ${index + 1} (${item.type.replace('_', ' ')}): "${item.text}"`
    ).join("\n");

    const languagesForPrompt = targetLanguages.join(", ");

    const prompt = `Translate the following texts from ${sourceLanguage} to these languages: ${languagesForPrompt}.
Keep the translations natural and contextually appropriate for a menu.

${textsForPrompt}

Important:
1. Return a valid JSON with this exact structure:
{
  "targetLanguage1": {
    "text1": "translated text 1 for language 1",
    "text2": "translated text 2 for language 1"
  },
  "targetLanguage2": {
    "text1": "translated text 1 for language 2",
    "text2": "translated text 2 for language 2"
  }
}

2. Each target language should be a property in the response
3. Inside each language property, include all translations with the exact keys "text1", "text2", etc.
4. Ensure translations are appropriate for menu context`;

    const result = await model.generateContent(prompt);
    const response = await result.response.text();

    // Extract JSON from response
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error("No valid JSON found in response");
    }

    // Clean and parse the JSON
    const jsonText = jsonMatch[0]
      .replace(/^```json\s*|\s*```$/g, "") // Remove code block markers
      .replace(/[\u200B-\u200D\uFEFF]/g, "") // Remove zero-width spaces
      .replace(/\n\s*\n/g, "\n") // Remove empty lines
      .replace(/,(\s*[}\]])/g, "$1") // Remove trailing commas
      .trim();
      
    const translations = JSON.parse(jsonText);
    
    // Validate the structure of the response
    for (const language of targetLanguages) {
      if (!translations[language]) {
        throw new Error(`Missing translation for language: ${language}`);
      }
      for (let i = 0; i < textsToTranslate.length; i++) {
        const textKey = `text${i+1}`;
        if (!translations[language][textKey]) {
          throw new Error(`Missing translation for ${textKey} in ${language}`);
        }
      }
    }
    
    // Restructure the response to match the expected return format
    const restructuredTranslations: Record<string, Record<string, string>> = {};
    
    for (const language of targetLanguages) {
      restructuredTranslations[language] = {};
      for (let i = 0; i < textsToTranslate.length; i++) {
        const textKey = `text${i+1}`;
        restructuredTranslations[language][textKey] = translations[language][textKey];
      }
    }
    
    // Track successful batch translation
    trackEvent('Individual Text Translated', {
      'Source Language': sourceLanguage,
      'Target Language(s)': targetLanguages,
      'Translation Type': textsToTranslate.map(t => t.type).join(', '),
      'Text Length (Source)': textsToTranslate.reduce((sum, t) => sum + t.text.length, 0),
      'Duration (s)': Math.round((Date.now() - startTime) / 1000),
      'Batch Request?': true,
      'Number of Texts in Batch': textsToTranslate.length
    });
    
    // Track credit usage for batch translation
    trackEvent('Credits Consumed', {
      'Amount': 1,
      'Reason': 'Individual Text Translation',
      'User ID': user.id
    });
    
    // Increment total translations performed (one per target language since batch counts as one translation per language)
    trackEvent('User Property Updated', {
      'Property': 'Total Translations Performed',
      'Increment By': targetLanguages.length
    });
    
    return restructuredTranslations;
  } catch (error) {
    console.error("Batch translation error:", error);
    
    // Track failed batch translation
    trackEvent('Individual Text Translation Failed', {
      'Source Language': sourceLanguage,
      'Target Language(s)': targetLanguages,
      'Translation Type': textsToTranslate.map(t => t.type).join(', '),
      'Text Length (Source)': textsToTranslate.reduce((sum, t) => sum + t.text.length, 0),
      'Error Message': error instanceof Error ? error.message : "Unknown error",
      'Duration (s)': Math.round((Date.now() - startTime) / 1000),
      'Batch Request?': true,
      'Number of Texts in Batch': textsToTranslate.length
    });
    
    throw new Error("Failed to translate texts. Please try again.");
  }
}
